import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button,  
    CircularProgress, Typography, FormControlLabel, Checkbox, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const TodoPage = () => {
    useAuthCheck();
    const { todoId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    
    const [formValues, setFormValues] = useState({
        todo: '',
        description: '',
        isActive: true,
    }); 
    
    useEffect(() => {     
        const fetchTodo = async () => {
            try {
                setLoading(true);
                
                if (todoId) {
                    const response = await api.get(`/todos/${todoId}`);

                    const formData = response?.data || {};
                    setFormValues({
                        todo: formData.todo || '',
                        description: formData.description || '',
                        isActive: formData.isActive ?? true,
                    })
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        fetchTodo();
    }, [todoId]);
    
    const checkoutSchema = yup.object().shape({
        todo: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = todoId ? "PUT" : "POST";
            const url = todoId
                ? `/params/update/${todoId}` 
                : '/params/new'

            await api({
                method,
                url,
                data: values,
            });
            
            toast.success(todoId 
                ? 'To do updated successfully.' 
                : 'To do created successfully.'
            );

            if (!todoId) {
                onSubmitProps.resetForm();
            }

            navigate("/params"); 
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save to do!");
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="To do"
                    subtitle={`${loading ? 'Loading...' : todoId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/todos")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Todo"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.todo}
                                        name="todo"
                                        error={!!touched.todo && !!errors.todo}
                                        helperText={touched.todo && errors.todo}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 12' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {todoId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default TodoPage;
